import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, toValue } from 'vue';

/**
 * Mounts a long list by chunks as the user scrolls: everything stays loaded in memory, only the
 * visible slice is rendered. Bind `sentinelRef` to an element placed after the last item and render
 * it only while `hasMore` is true.
 *
 * @param {import('vue').MaybeRefOrGetter<Array>} items full list, already sorted/filtered
 * @param {import('vue').MaybeRefOrGetter<number>} chunkSize items revealed per step (0 = render all)
 */
export function useProgressiveList(items, chunkSize) {
	const sentinelRef = ref(null);
	let observer = null;

	// Without an IntersectionObserver nothing can reveal the next chunk, and reset() would keep clamping
	// the list back to its first one on every items change. Neutralising the step instead renders
	// everything up front: no sentinel, no spinner, no list stranded at 40 rows.
	const canRevealOnScroll = typeof IntersectionObserver === 'function';

	const step = () => (canRevealOnScroll ? Math.max(0, Number(toValue(chunkSize)) || 0) : 0);
	const list = () => {
		const value = toValue(items);
		return Array.isArray(value) ? value : [];
	};
	const initialCount = () => (step() > 0 ? step() : Number.MAX_SAFE_INTEGER);

	const visibleCount = ref(initialCount());
	const visibleItems = computed(() => (step() > 0 ? list().slice(0, visibleCount.value) : list()));
	const hasMore = computed(() => step() > 0 && visibleCount.value < list().length);

	function reobserve() {
		const element = sentinelRef.value;
		if (!element || !observer) {
			return;
		}

		// Growing the list does not change the sentinel's intersection state, so the observer would stay
		// silent after the first hit. Re-observing forces a fresh callback and keeps filling until the
		// sentinel actually leaves the viewport.
		observer.unobserve(element);
		observer.observe(element);
	}

	async function revealMore() {
		if (!hasMore.value) {
			return;
		}

		visibleCount.value = Math.min(visibleCount.value + step(), list().length);
		await nextTick();
		reobserve();
	}

	async function reset() {
		visibleCount.value = initialCount();
		await nextTick();
		reobserve();
	}

	onMounted(() => {
		if (!canRevealOnScroll) {
			return;
		}

		observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					revealMore();
				}
			},
			{ rootMargin: '200px' }
		);

		if (sentinelRef.value) {
			observer.observe(sentinelRef.value);
		}
	});

	onBeforeUnmount(() => {
		observer?.disconnect();
		observer = null;
	});

	watch(sentinelRef, (element, previousElement) => {
		if (previousElement && observer) {
			observer.unobserve(previousElement);
		}
		if (element && observer) {
			observer.observe(element);
		}
	});

	watch(() => toValue(items), reset);

	return { sentinelRef, visibleItems, hasMore, reset };
}
